const RESULT_VARIANT = {
  QUOTE_EXPIRED: 'quote-expired',
  INSUFFICIENT_BALANCE: 'insufficient-balance',
  GENERIC: 'generic'
};

// Backend codes from basquet-backend src/api/v1/services/walletConversion/walletConversion.errors.ts.
// The result describes which screen to render; `description` is the fallback
// shown when the backend doesn't return its own `message`.
const ERROR_CODE_TO_RESULT = {
  QUOTE_EXPIRED: {
    variant: RESULT_VARIANT.QUOTE_EXPIRED,
    title: 'Quote expired',
    description: 'The quote expired before confirmation. Generate a new quote.'
  },
  QUOTE_NOT_FOUND: {
    variant: RESULT_VARIANT.QUOTE_EXPIRED,
    title: 'Quote expired',
    description: 'The quote is no longer valid. Generate a new quote.'
  },
  RATE_EXPIRED: {
    variant: RESULT_VARIANT.QUOTE_EXPIRED,
    title: 'Rate expired',
    description: 'The rate is no longer valid. Generate a new quote.'
  },
  RATE_INVALID: {
    variant: RESULT_VARIANT.QUOTE_EXPIRED,
    title: 'Rate unavailable',
    description: 'The rate is unavailable. Generate a new quote.'
  },
  RATE_MISMATCH: {
    variant: RESULT_VARIANT.QUOTE_EXPIRED,
    title: 'Rate changed',
    description: 'The rate changed before confirmation. Generate a new quote.'
  },
  INSUFFICIENT_BALANCE: {
    variant: RESULT_VARIANT.INSUFFICIENT_BALANCE,
    title: 'Insufficient balance',
    description: 'Your wallet balance is too low for this conversion.'
  },
  QUOTE_MISMATCH: {
    variant: RESULT_VARIANT.GENERIC,
    title: 'Conversion not allowed',
    description: 'This quote does not belong to your account.'
  },
  CONVERSION_FAILED: {
    variant: RESULT_VARIANT.GENERIC,
    title: 'Conversion failed',
    description: 'The conversion could not be completed. Please try again.'
  },
  INVALID_REQUEST: {
    variant: RESULT_VARIANT.GENERIC,
    title: 'Invalid request',
    description: 'The request could not be processed. Please try again.'
  }
};

const GENERIC_FALLBACK = {
  variant: RESULT_VARIANT.GENERIC,
  title: 'Something went wrong',
  description: 'An unexpected error occurred. Please try again.'
};

// A code is "known-safe" when it's one of our recognised domain errors.
// Only those carry a backend `message` we trust to show the user. Unknown
// codes (network timeouts, upstream 5xx) can carry raw infra detail such
// as "connection timed out: /10.20.5.54:8218", which must never reach the
// merchant — so we fall back to curated copy for them.
const isKnownErrorCode = (code) => Object.prototype.hasOwnProperty.call(ERROR_CODE_TO_RESULT, code);

const mapErrorToResult = (code, message) => {
  const entry = ERROR_CODE_TO_RESULT[code] || GENERIC_FALLBACK;
  const useBackendMessage = isKnownErrorCode(code) && Boolean(message?.trim());
  return {
    variant: entry.variant,
    title: entry.title,
    description: useBackendMessage ? message : entry.description
  };
};

// Short inline message for the amount-step quote failure. Same known-safe
// rule: trust the backend copy only for recognised codes, otherwise a
// neutral retry prompt — never the raw error string.
const QUOTE_ERROR_FALLBACK = 'We could not fetch a quote. Please try again.';

const mapQuoteErrorMessage = (code, message) => {
  if (isKnownErrorCode(code)) {
    return message?.trim() ? message : ERROR_CODE_TO_RESULT[code].description;
  }
  return QUOTE_ERROR_FALLBACK;
};

const readError = (error) => {
  const body = error?.response?.data;
  if (body && typeof body === 'object') {
    return {
      code: typeof body.status === 'string' ? body.status : 'UNKNOWN',
      message: typeof body?.data?.message === 'string' ? body.data.message : ''
    };
  }
  return { code: 'UNKNOWN', message: error?.message || '' };
};

export {
  ERROR_CODE_TO_RESULT,
  GENERIC_FALLBACK,
  mapErrorToResult,
  mapQuoteErrorMessage,
  readError,
  RESULT_VARIANT
};
